/**
 * Persistance de la configuration du dashboard (scope utilisateur).
 *
 * Le front est codé contre une interface `DashboardConfigStore` :
 * - `apiStore`   : persistance serveur réelle (VM_DATA, /dashboard/config)
 * - `localStore` : persistance navigateur, pratique pour le dev hors-ligne
 *
 * Bascule via REACT_APP_DASHBOARD_LOCAL=true (sinon serveur par défaut).
 */
import {
  DASHBOARD_CONFIG_VERSION,
  DashboardConfig,
} from "../components/screens/dashboard/dashboardConfig";
import { fetchWithToken } from "../utils/fetchWithToken";

export interface DashboardConfigStore {
  /** Config du user (null si aucune). */
  load(): Promise<DashboardConfig | null>;
  /** Upsert de la config user (snapshot). */
  save(config: DashboardConfig): Promise<void>;
  /** Supprime la config user (retour au layout par défaut). */
  reset(): Promise<void>;
  /** Sauvegarde best-effort qui survit au déchargement de page (F5 / fermeture). */
  flush(config: DashboardConfig): void;
}

const API_URL = `${process.env.REACT_APP_URL_SERVER_DATA}/dashboard/config`;

const apiStore: DashboardConfigStore = {
  async load() {
    const res = await fetchWithToken(API_URL, { method: "GET" });
    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const parsed = (await res.json()) as DashboardConfig;
    // Garde-fou : ignore une config d'une version inconnue (futures migrations).
    if (parsed.version !== DASHBOARD_CONFIG_VERSION) return null;
    return parsed;
  },
  async save(config) {
    const res = await fetchWithToken(API_URL, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
  async reset() {
    const res = await fetchWithToken(API_URL, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  },
  flush(config) {
    // keepalive : la requête peut se terminer après le déchargement de la page.
    const token = localStorage.getItem("accessToken");
    fetch(API_URL, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(config),
      keepalive: true,
      credentials: "include",
    }).catch(() => undefined);
  },
};

const LOCAL_KEY = "dashboard-config";

const localStore: DashboardConfigStore = {
  async load() {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as DashboardConfig;
      if (parsed.version !== DASHBOARD_CONFIG_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  },
  async save(config) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(config));
  },
  async reset() {
    localStorage.removeItem(LOCAL_KEY);
  },
  flush(config) {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(config));
  },
};

const store: DashboardConfigStore =
  process.env.REACT_APP_DASHBOARD_LOCAL === "true" ? localStore : apiStore;

export const fetchDashboardConfig = (): Promise<DashboardConfig | null> =>
  store.load();

export const saveDashboardConfig = (config: DashboardConfig): Promise<void> =>
  store.save(config);

export const resetDashboardConfig = (): Promise<void> => store.reset();

export const flushDashboardConfig = (config: DashboardConfig): void =>
  store.flush(config);
